// MeetingsSection — Presentational "Business Meetings" section for the
// EditTripModal: the section header (Building2 icon + "Business Meetings" title,
// the count badge, the "Clear All" button, and the "Add Meeting" button) plus
// the body, which is either the empty-state block ("No business meetings
// scheduled") or the list of MeetingRow cards.
//
// Extracted verbatim from `EditTripModal.js` (the "Business Meetings Section"
// block) as part of the behavior-preserving refactor (issue #240). The
// visibility conditionals come from `getMeetingsSummary(editedTrip)` and are
// passed in as the `summary` prop ({ count, showBadge, showClearAll, isEmpty })
// so this component stays render-only. The source's "Clear All" button and the
// per-row Edit/Delete handlers wrapped `window.confirm(...)` + state updates;
// those side effects move to the container, surfaced here as the `onClearAll`,
// `onEditMeeting`, and `onDeleteMeeting` callbacks. Wrapped in React.memo.
// Button labels ("Clear All", "Add Meeting"), the badge, the empty-state text,
// the render guards (`showBadge`, `showClearAll`, `isEmpty`), the inline hover
// handlers, icons (lucide-react), and styles are identical to the source.
//
// Props:
//   summary        - { count, showBadge, showClearAll, isEmpty } from getMeetingsSummary
//   meetings       - the array of meeting objects to render as rows
//   onClearAll     - () => void invoked by the "Clear All" button
//   onAddMeeting   - () => void invoked by the "Add Meeting" button
//   onEditMeeting  - (meeting) => void invoked by a row's Edit button
//   onDeleteMeeting- (meetingId) => void invoked by a row's Delete button
//
// Validates: Requirements 3.3, 3.4, 1.2, 1.6

import React from 'react';
import { Plus, Trash2, Building2 } from 'lucide-react';
import MeetingRow from './MeetingRow';

export const MeetingsSection = React.memo(function MeetingsSection({
  summary,
  meetings,
  onClearAll,
  onAddMeeting,
  onEditMeeting,
  onDeleteMeeting
}) {
  return (
    <div style={{ marginBottom: '24px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1f2937',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Building2 size={18} style={{ color: '#7c3aed' }} />
            Business Meetings
            {summary.showBadge && (
              <span style={{
                fontSize: '12px',
                color: '#6b7280',
                backgroundColor: '#f3f4f6',
                padding: '2px 8px',
                borderRadius: '12px',
                fontWeight: '500'
              }}>
                {summary.count}
              </span>
            )}
          </h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            {summary.showClearAll && (
              <button
                onClick={() => onClearAll()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  backgroundColor: 'white',
                  color: '#dc2626',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#fef2f2';
                  e.target.style.borderColor = '#f87171';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.borderColor = '#fecaca';
                }}
                title="Delete all meetings"
              >
                <Trash2 size={14} />
                Clear All
              </button>
            )}
            <button
              onClick={() => onAddMeeting()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 16px',
                backgroundColor: '#0ea5e9',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#0284c7'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#0ea5e9'}
            >
              <Plus size={16} />
              Add Meeting
            </button>
          </div>
        </div>

      {summary.isEmpty ? (
        <div style={{
          textAlign: 'center',
          padding: '32px',
          color: '#6b7280'
        }}>
          <Building2 size={48} style={{ 
            color: '#d1d5db', 
            margin: '0 auto 12px auto' 
          }} />
          <p style={{ fontSize: '14px', marginBottom: '4px' }}>No business meetings scheduled</p>
          <p style={{ fontSize: '12px', color: '#9ca3af' }}>Add meetings to track your business appointments</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {meetings.map((meeting, _index) => (
            <MeetingRow
              key={meeting.id}
              meeting={meeting}
              onEdit={() => onEditMeeting(meeting)}
              onDelete={() => onDeleteMeeting(meeting.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
});

export default MeetingsSection;
